/* eslint-disable react/jsx-no-comment-textnodes */
/* eslint-disable react-hooks/exhaustive-deps */
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { ConceptType, useDropLocations } from "../../store/useDropLocations";
import { useMarketPricing } from "../../store/useMarketPricing";
import { OneTimeUsers } from "./components/OneTimeUsers";
import { ActiveUsersPerLocation } from "./components/ActiveUsersPerLocation";
import { PercentageActiveUsers } from "./components/PercentageActiveUsers";
import { ParticipationInCatchmentArea } from "./components/ParticipationInCatchmentArea";
import { Revenue } from "./components/Revenue";
import { MaterialsRevenue } from "./components/MaterialsRevenue";
import { StatieRevenue } from "./components/StatieRevenue";
import { ParcelsRevenue } from "./components/ParcelsRevenue";
import { AverageRevenuePerKgCollectedMaterial } from "./components/AverageRevenuePerKgCollectedMaterial";
import { AverageConsumerPaymentPerKgCollectedMaterial } from "./components/AverageConsumerPaymentPerKgCollectedMaterial";
import { MaterialVolume } from "./components/MaterialVolume";
import { MaterialItemVolume } from "./components/MaterialItemVolume";
import { DepositReturn } from "./components/DepositReturn";
import { ParcelVolume } from "./components/ParcelVolume";
import { PercentageStatieOverTotalDrops } from "./components/PercentageStatieOverTotalDrops";
import { PercentageStatieVanPros } from "./components/PercentageStatieVanPros";
import { CheckboxGrid, FormSelect, PrimaryButton } from "../../components";
import dayjs from "dayjs";
import { getWeekRange } from "../../services/dateService";
import { InactiveUsersPerLocation } from "./components/InactiveUsersPerLocation";
import { Churn } from "./components/Churn";
import { Tooltip } from "@material-tailwind/react";
import { RegularDroppersPerLocation } from "./components/RegularDroppersPerLocation";

export const StoreKPIsTable = () => {
  /** Hooks */
  const {
    allTimeLocationStats: initialLocationStats,
    yearlyLocationStats: initialYearlyLocationStats,
    quarterlyLocationStats: initialQuarterlyLocationStats,
    monthlyLocationStats: initialMonthlyLocationStats,
    weeklyLocationStats: initialWeeklyLocationStats,
    weeksAfterOpeningLocationStats: initialWeeksAfterOpeningLocationStats,
    dropLocations,
    recalculateAllTimeLocationStats,
    recalculateYearlyLocationStats,
    recalculateQuarterlyLocationStats,
    recalculateMonthlyLocationStats,
    recalculateWeeklyLocationStats,
    recalculateWeeksAfterOpeningLocationStats,
  } = useDropLocations();
  const { marketPrices, getAllMarketPrices } = useMarketPricing();
  /** State */
  const [period, setPeriod] = useState<
    "all" | "yearly" | "quarterly" | "monthly" | "weekly" | "weeksAfterOpening"
  >("all");
  const [year, setYear] = useState<number>(dayjs().year());
  const [month, setMonth] = useState<number>(dayjs().month());
  const [quarter, setQuarter] = useState<"Q1" | "Q2" | "Q3" | "Q4">("Q1");
  const [week, setWeek] = useState<Date>(new Date());
  const [weeksAfterOpening, setWeeksAfterOpening] = useState<number>(4);
  const [storeType, setStoreType] = useState<"all" | "fs" | "ss" | "c">("all");
  const [storeCity, setStoreCity] = useState<string>("all");
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  /** Constants */
  const { startOfWeek, endOfWeek } = getWeekRange(week);

  /** Callbacks */
  const getLastWeek = () => setWeek(new Date(week.setDate(week.getDate() - 7)));
  const getNextWeek = () => setWeek(new Date(week.setDate(week.getDate() + 7)));

  const recalculateStats = useCallback(() => {
    if (period === "yearly") return recalculateYearlyLocationStats(year);
    if (period === "quarterly")
      return recalculateQuarterlyLocationStats(year, quarter);
    if (period === "monthly") {
      return recalculateMonthlyLocationStats(year, Number(month) + 1);
    }
    if (period === "weekly") {
      return recalculateWeeklyLocationStats(startOfWeek, endOfWeek);
    }
    if (period === "weeksAfterOpening") {
      return recalculateWeeksAfterOpeningLocationStats(weeksAfterOpening);
    }
    return recalculateAllTimeLocationStats();
  }, [period, year, quarter, month, weeksAfterOpening, startOfWeek, endOfWeek]);

  const initialStats = useMemo(() => {
    if (period === "yearly") return initialYearlyLocationStats;
    if (period === "quarterly") return initialQuarterlyLocationStats;
    if (period === "monthly") return initialMonthlyLocationStats;
    if (period === "weekly") return initialWeeklyLocationStats;
    if (period === "weeksAfterOpening")
      return initialWeeksAfterOpeningLocationStats;
    return initialLocationStats;
  }, [
    initialLocationStats,
    initialYearlyLocationStats,
    initialQuarterlyLocationStats,
    initialMonthlyLocationStats,
    initialWeeklyLocationStats,
    initialWeeksAfterOpeningLocationStats,
    period,
  ]);

  const storesToInclude = useMemo(() => {
    let includeStores = [];
    for (let location of dropLocations ?? []) {
      if (initialStats.some((stat) => stat?.location?._id === location?._id)) {
        includeStores.push(location);
      }
    }
    return includeStores;
  }, [dropLocations, initialStats]);

  const dropdownData = useMemo(() => {
    const uniqueCities = Array.from(
      new Map((dropLocations ?? []).map((item) => [item.city, item])).values()
    );
    const finalCities =
      uniqueCities?.map((location) => ({
        label: location?.city as string,
        value: location?.city as string,
      })) || [];
    finalCities.unshift({ label: "All cities", value: "all" });
    return finalCities;
  }, [dropLocations]);

  const locationStats = useMemo(() => {
    let finalLocationStats = initialStats;
    /** Filter by year */
    if (period === "yearly") {
      finalLocationStats = finalLocationStats?.filter(
        (location) => location?.year === Number(year)
      );
    }
    if (period === "quarterly") {
      finalLocationStats = finalLocationStats?.filter(
        (location) =>
          location?.quarter === quarter && location?.year === Number(year)
      );
    }
    if (period === "monthly") {
      finalLocationStats = finalLocationStats?.filter(
        (location) =>
          location?.month - 1 === Number(month) &&
          location?.year === Number(year)
      );
    }
    if (period === "weekly") {
      const startYear = startOfWeek.getFullYear();
      const startMonth = startOfWeek.getMonth() + 1;
      const startDay = startOfWeek.getDate();
      const endYear = endOfWeek.getFullYear();
      const endMonth = endOfWeek.getMonth() + 1;
      const endDay = endOfWeek.getDate();
      finalLocationStats = finalLocationStats?.filter(
        (location) =>
          Number(location?.startWeekDay) === Number(startDay) &&
          Number(location?.startWeekMonth) === Number(startMonth) &&
          Number(location?.startWeekYear) === Number(startYear) &&
          Number(location?.endWeekDay) === Number(endDay) &&
          Number(location?.endWeekMonth) === Number(endMonth) &&
          Number(location?.endWeekYear) === Number(endYear)
      );
    }
    if (period === "weeksAfterOpening") {
      finalLocationStats = finalLocationStats?.filter(
        (location) =>
          Number(location?.weeksAfterOpening) === Number(weeksAfterOpening)
      );
    }
    /**Filter by store type */
    if (storeType && storeType !== "all") {
      finalLocationStats = finalLocationStats?.filter(
        // @ts-ignore
        (location) => location?.location?.conceptType === storeType
      );
    }
    /** Filter by store city */
    if (storeCity && storeCity !== "all") {
      finalLocationStats = finalLocationStats?.filter(
        // @ts-ignore
        (location) => location?.location?.city === storeCity
      );
    }
    finalLocationStats = finalLocationStats?.filter((location) =>
      selectedStores?.includes(location?.location?.cost_place as string)
    );
    return finalLocationStats;
  }, [
    initialStats,
    storeType,
    storeCity,
    period,
    year,
    quarter,
    month,
    startOfWeek,
    endOfWeek,
    weeksAfterOpening,
    selectedStores,
  ]);

  const transformData = (originalData: any[]) => {
    // First, get unique materials
    const materials = new Set();
    originalData.forEach((location) => {
      location.materialsVolume.forEach((material: any) => {
        materials.add(material.material);
      });
    });

    // Transform the data
    const transformedData = Array.from(materials).map((materialId) => {
      const materialInfo = {
        materialId,
        // You can add a mapping here for material names
        materialName: getMaterialName(materialId as string), // You'll need to implement this function
        locations: [],
      };

      originalData.forEach((locationData) => {
        // Find volume for this material in this location
        const materialVolume = locationData.materialsVolume.find(
          (m: any) => m.material === materialId
        );

        const locationInfo = {
          locationId: locationData.location._id,
          locationName: locationData.location.cost_place,
          volume: materialVolume ? materialVolume.volume : 0,
        };

        // @ts-ignore
        materialInfo.locations.push(locationInfo);
      });

      return materialInfo;
    });

    return transformedData;
  };

  // Example material name mapping function
  const getMaterialName = (materialId: string) => {
    const materialName =
      marketPrices?.find((material) => material._id === materialId)?.name ??
      materialId;
    return materialName;
  };
  const transformedData = useMemo(() => {
    return transformData(locationStats);
  }, [marketPrices, locationStats]);

  const selectStoreType = (e: any) => setStoreType(e?.target?.value);
  const selectStoreCity = (e: any) => setStoreCity(e?.target?.value);
  const selectPeriod = (e: any) => setPeriod(e?.target?.value);
  const selectYear = (e: any) => setYear(e?.target?.value);
  const selectMonth = (e: any) => setMonth(e?.target?.value);
  const selectQuarter = (e: any) => setQuarter(e?.target?.value);
  const selectWeeksAfterOpening = (e: any) =>
    setWeeksAfterOpening(e?.target?.value);

  const onSelectFilters = useCallback(
    (option: string) => {
      if (!setSelectedStores || !selectedStores) return;
      if (option === "All") {
        if (selectedStores.includes("All")) {
          setSelectedStores([]);
          return;
        }
        setSelectedStores([
          "All",
          ...(storesToInclude?.length
            ? storesToInclude?.map((location) => location?.cost_place as string)
            : []),
        ]);
        return;
      }
      setSelectedStores((prev: any) =>
        prev.includes(option)
          ? prev
              .filter((item: any) => item !== option)
              ?.filter((item: any) => item !== "All")
          : [...prev, option]?.filter((item: any) => item !== "All")
      );
    },
    [storesToInclude, selectedStores]
  );

  /** Effects */
  useEffect(() => {
    getAllMarketPrices();
  }, []);

  useEffect(() => {
    setSelectedStores([
      "All",
      ...(storesToInclude?.length
        ? storesToInclude?.map((location) => location?.cost_place as string)
        : []),
    ]);
  }, [storesToInclude]);

  return (
    <>
      <div className="flex flex-col w-full p-2 sm:p-7 bg-blue-200 border border-gray-border drop-shadow-md mb-1 sm:mb-2 mt-4">
        <p
          className={`font-satoshi font-bold text-lg sm:text-2xl text-dark mb-6`}
        >
          Definitions
        </p>
        <p className={`font-satoshi text-base text-dark mb-1`}>
          - <b>Dropper</b> - Users that have made at least 1 drop
        </p>
        <p className={`font-satoshi text-base text-dark mb-1`}>
          - <b>Regular Dropper</b> - Users that have made at least 2 drops
        </p>
        <p className={`font-satoshi text-base text-dark mb-1`}>
          - <b>Active Dropper</b> - Users that have made at least 2 drops, and
          have made a drop in the last 3 months
        </p>
        <p className={`font-satoshi text-base text-dark mb-1`}>
          - <b>Inactive Dropper</b> - Users that have made at least 2 drops, but
          have not made a drop in the last 3 months
        </p>
      </div>
      <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl border-gray-border drop-shadow-none mb-1 sm:mb-2 mt-4">
        {/* Title */}
        <div className="flex flex-row items-start justify-between">
          <p
            className={`font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-12`}
          >
            Filters
          </p>
          <div>
            <PrimaryButton
              onClick={recalculateStats}
              label="Recalculate stats"
              className="h-10"
            />
            {locationStats?.length ? (
              <p className="font-satoshi text-xs text-dark mt-4 text-right">
                Last updated on:{" "}
                {dayjs(locationStats?.[0]?.updatedAt)?.format(
                  "MMM DD YYYY HH:mm:ss"
                )}
              </p>
            ) : (
              <p className="font-satoshi text-xs text-dark mt-4 text-right">
                There is no stat registered for this filter
              </p>
            )}
          </div>
        </div>
        {/* Filters */}
        <div className="flex flex-row items-start justify-start">
          <div className="flex flex-col">
            <FormSelect
              label="Select Location store type"
              placeholder="Select store type"
              value={storeType ?? ""}
              // @ts-ignore
              options={
                Object.entries({ "All types": "all", ...ConceptType }).map(
                  ([key, value]) => ({
                    value: value,
                    label: key,
                  })
                ) ?? []
              }
              onChange={selectStoreType}
            />
            <div className="ml-6" />
            <FormSelect
              label="Select Location city"
              placeholder="Select location city"
              value={storeCity ?? ""}
              // @ts-ignore
              options={dropdownData ?? []}
              onChange={selectStoreCity}
            />
          </div>
          <div className="ml-10" />
          <div className="flex flex-col border-l border-gray-border px-10">
            <FormSelect
              label="Select period type"
              placeholder="Select period type"
              value={period ?? ""}
              // @ts-ignore
              options={
                [
                  "All",
                  "Yearly",
                  "Quarterly",
                  "Monthly",
                  "Weekly",
                  "Weeks after opening",
                ].map((value) => ({
                  value:
                    value === "Weeks after opening"
                      ? "weeksAfterOpening"
                      : value?.toLowerCase(),
                  label: value,
                })) ?? []
              }
              onChange={selectPeriod}
            />
            <div className="flex flex-row items-center">
              {period !== "all" &&
              period !== "weeksAfterOpening" &&
              period !== "weekly" ? (
                <FormSelect
                  label="Select period year"
                  placeholder="Select period year"
                  value={year}
                  // @ts-ignore
                  options={
                    Array.from(
                      { length: dayjs().year() - 2023 },
                      (_, i) => dayjs().year() - i
                    ).map((value) => ({
                      value,
                      label: value,
                    })) ?? []
                  }
                  onChange={selectYear}
                />
              ) : period !== "weekly" ? (
                <div className="opacity-0">
                  <FormSelect
                    disabled
                    label="Select period year"
                    placeholder="Select period year"
                    value={year}
                    // @ts-ignore
                    options={
                      Array.from(
                        { length: dayjs().year() - 2023 },
                        (_, i) => dayjs().year() - i
                      ).map((value) => ({
                        value,
                        label: value,
                      })) ?? []
                    }
                    onChange={selectYear}
                  />
                </div>
              ) : null}
              {period === "monthly" ? (
                <>
                  <div className="ml-6" />
                  <FormSelect
                    label="Select period month"
                    placeholder="Select period month"
                    value={month}
                    // @ts-ignore
                    options={
                      [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((value) => ({
                        label: dayjs().month(value).format("MMMM"),
                        value,
                      })) ?? []
                    }
                    onChange={selectMonth}
                  />
                </>
              ) : null}
              {period === "quarterly" ? (
                <>
                  <div className="ml-6" />
                  <FormSelect
                    label="Select period quarter"
                    placeholder="Select period quarter"
                    value={quarter}
                    // @ts-ignore
                    options={
                      ["Q1", "Q2", "Q3", "Q4"].map((value) => ({
                        label: value,
                        value,
                      })) ?? []
                    }
                    onChange={selectQuarter}
                  />
                </>
              ) : null}
              {period === "weekly" ? (
                <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px] mb-2 sm:mb-0 max-w-[300px]">
                  <PrimaryButton size="sm" onClick={getLastWeek} label="Prev" />
                  <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
                    {startOfWeek.toDateString()} - {endOfWeek.toDateString()}
                  </div>
                  <PrimaryButton
                    size="sm"
                    disabled={endOfWeek > new Date()}
                    onClick={getNextWeek}
                    label="Next"
                  />
                </div>
              ) : null}
              {period === "weeksAfterOpening" ? (
                <>
                  <FormSelect
                    label="Select weeks after opening"
                    placeholder="Select weeks after opening"
                    value={weeksAfterOpening}
                    // @ts-ignore
                    options={
                      [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(
                        (value) => ({
                          label: value,
                          value,
                        })
                      ) ?? []
                    }
                    onChange={selectWeeksAfterOpening}
                  />
                </>
              ) : null}
            </div>
          </div>
          <div className="flex flex-1 items-center justify-end">
            <CheckboxGrid
              label="Select store to include in the stats"
              options={[
                "All",
                ...(storesToInclude?.map(
                  (location) => location?.cost_place as string
                ) ?? []),
              ]}
              selectedOptions={selectedStores ?? []}
              onSelect={onSelectFilters}
            />
          </div>
        </div>
      </div>
      <div className="w-full bg-white border border-gray-border drop-shadow-md">
        <div className="overflow-x-auto font-satoshi">
          <table className="table-auto border-collapse border border-gray-300 w-full text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th
                  className="border border-gray-300 px-4 py-2 text-left"
                  colSpan={1}
                >
                  Store KPI's
                </th>
                {/* Store names */}
                {locationStats.map((location) => (
                  <th
                    key={location._id}
                    className="border border-gray-300 px-4 py-2 text-center"
                    colSpan={period === "weekly" ? 1 : 2}
                  >
                    {location?.location?.cost_place}
                  </th>
                ))}
              </tr>
              <tr className="bg-gray-200">
                <th className="border border-gray-300 px-4 py-2 text-left">
                  KPI's
                </th>
                {/* Store targets and actuals */}
                {locationStats.map((location) => {
                  if (period === "weekly") {
                    return (
                      <th
                        key={location._id}
                        className="border border-gray-300 px-4 py-2 text-center"
                      >
                        Actual
                      </th>
                    );
                  }
                  return (
                    <Fragment key={location._id}>
                      <th className="border border-gray-300 px-4 py-2 text-center">
                        Target
                      </th>
                      <th className="border border-gray-300 px-4 py-2 text-center">
                        Actual
                      </th>
                    </Fragment>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {/* All users */}
              {/* <tr>
                <td className="border border-gray-300 px-4 py-2">
                  All users
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <AllUsers showTarget={period !== 'weekly'} key={location._id} location={location} />
                ))}
              </tr> */}
              {/* Total Droppers per location */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Total Droppers per location
                  <span className="ml-1 inline-block">
                    <Tooltip
                      content="Total unique users who made at least one drop"
                      placement="bottom"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <OneTimeUsers
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
              {/* Regular Droppers per location */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Regular Droppers per location
                  <span className="ml-1 inline-block">
                    <Tooltip content="Users with 2+ drops" placement="bottom">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <RegularDroppersPerLocation
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
              {/* Active users per location */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Active Droppers per location
                  <span className="ml-1 inline-block">
                    <Tooltip
                      content="Users with 2+ drops and that have made at least 1 drop in last 3 months"
                      placement="bottom"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <ActiveUsersPerLocation
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
              {/* Inactive users per location */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Inactive Droppers per location
                  <span className="ml-1 inline-block">
                    <Tooltip
                      content="Users with 2+ drops and who haven't made a drop in the last 30 days"
                      placement="bottom"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <InactiveUsersPerLocation
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
              {/* Percentage active users */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Percentage active users
                  <span className="ml-1 inline-block">
                    <Tooltip
                      content="Percentage of active users over total users"
                      placement="bottom"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <PercentageActiveUsers
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
              {/* Churn */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Churn
                  <span className="ml-1 inline-block">
                    <Tooltip
                      content="inactive users / active + inactive"
                      placement="bottom"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <Churn
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
              {/* Participation in catchment area */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Participation in catchment area
                  <span className="ml-1 inline-block">
                    <Tooltip
                      content="Participation in catchment area"
                      placement="bottom"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <ParticipationInCatchmentArea
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
              {/* Revenue */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Revenue
                  <span className="ml-1 inline-block">
                    <Tooltip
                      content="Total revenue from all sources (materials, statiegeld, parcels)"
                      placement="bottom"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <Revenue
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
              {/* Materials Revenue */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Materials Revenue
                  <span className="ml-1 inline-block">
                    <Tooltip
                      content="Revenue from material drops"
                      placement="bottom"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <MaterialsRevenue
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
              {/* Statie Revenue */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Statie Revenue
                  <span className="ml-1 inline-block">
                    <Tooltip
                      content="Revenue from Statie drops"
                      placement="bottom"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <StatieRevenue
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
              {/* Parcels Revenue */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Parcels Revenue
                  <span className="ml-1 inline-block">
                    <Tooltip content="Revenue from parcels" placement="bottom">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <ParcelsRevenue
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
              {/* Average revenue per kg collected material */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Average revenue per kg collected material
                  <span className="ml-1 inline-block">
                    <Tooltip
                      content="Total materials revenue / Total kg collected"
                      placement="bottom"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <AverageRevenuePerKgCollectedMaterial
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
              {/* Average consumer payment per kg collected material */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Average consumer payment per kg collected material
                  <span className="ml-1 inline-block">
                    <Tooltip
                      content="Total consumer payments / Total kg collected"
                      placement="bottom"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <AverageConsumerPaymentPerKgCollectedMaterial
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
              {/* Material volume (kg) */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Material volume (kg)
                  <span className="ml-1 inline-block">
                    <Tooltip
                      content="Total weight of materials collected in kilograms"
                      placement="bottom"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <MaterialVolume
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
              {transformedData?.map((item) => (
                <tr key={String(item?.materialId)}>
                  <td className="border border-gray-300 px-4 py-2 text-right">
                    {item?.materialName} volume (kg)
                  </td>
                  {item?.locations.map(
                    (location: {
                      locationId: string;
                      volume: number;
                      locationName: string;
                    }) => (
                      // @ts-ignore
                      <MaterialItemVolume
                        key={location.locationId}
                        showTarget={period !== "weekly"}
                        location={location}
                      />
                    )
                  )}
                </tr>
              ))}
              {/* Depostit return (items) */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Depostit return (items)
                  <span className="ml-1 inline-block">
                    <Tooltip
                      content="Number of deposit bottles/cans returned"
                      placement="bottom"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <DepositReturn
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
              {/* Parcel volume (pick-ups & drop-offs) */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Parcel volume (pick-ups & drop-offs)
                  <span className="ml-1 inline-block">
                    <Tooltip
                      content="Total number of parcels picked up and dropped off"
                      placement="bottom"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <ParcelVolume
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
              {/* Traffic from parcels */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Traffic from parcels
                  <span className="ml-1 inline-block">
                    <Tooltip
                      content="Additional customer visits generated by parcel services"
                      placement="bottom"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => {
                  if (period === "weekly") {
                    return (
                      <th
                        key={location._id}
                        className="border border-gray-300 px-4 py-2 text-center"
                      >
                        {0}
                        {/* TODO: calculate this value */}
                      </th>
                    );
                  }
                  return (
                    <Fragment key={location._id}>
                      <td className="border border-gray-300 px-4 py-2 text-center">
                        {0}
                        {/* TODO: calculate this value */}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-center">
                        {0}
                        {/* TODO: calculate this value */}
                      </td>
                    </Fragment>
                  );
                })}
              </tr>
              {/* Percentage Statie over total drops */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Percentage Statie over total drops
                  <span className="ml-1 inline-block">
                    <Tooltip
                      content="Statiegeld drops / Total drops × 100%"
                      placement="bottom"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <PercentageStatieOverTotalDrops
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
              {/* Percentage Statie van profs */}
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  Percentage Statie van profs
                  <span className="ml-1 inline-block">
                    <Tooltip
                      content="Professional statiegeld collectors / Total statiegeld drops × 100%"
                      placement="bottom"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-4 h-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </Tooltip>
                  </span>
                </td>
                {locationStats.map((location) => (
                  // @ts-ignore
                  <PercentageStatieVanPros
                    showTarget={period !== "weekly"}
                    key={location._id}
                    location={location}
                  />
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
